/**
 * Credit-bridge client state.
 *
 * ADR-0008 (Sprint 14 — client scaffolding only). The edge function in
 * main-Jubit doesn't exist yet; this store is guarded by `isBridgeEnabled()`
 * which checks `import.meta.env.VITE_BRIDGE_URL`. When that's unset (default),
 * the store is inert — connect/disconnect no-op, queue drain no-op — and the
 * Settings UI shows the "coming soon" copy.
 *
 * When the env var ships (post Sprint 15), the store starts queuing POSTs.
 *
 * Persistence: zustand/middleware. localStorage survives reloads so a clear
 * recorded offline still posts when the user reconnects.
 *
 * Not-in-this-sprint:
 *   - actual OAuth popup UX (stub returns a demo token)
 *   - retry backoff tuning beyond a simple 60s window
 *   - server-acked idempotency dedupe (relies on 24h server cache per ADR-0008)
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { openJubitOAuth, BridgeOAuthError } from '../auth/jubitOAuth.ts';

export type BridgeLinkStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

/** ADR-0011: towers submitted for server re-simulation. */
export interface BridgeTowerRef {
  specId: string;
  x: number;
  y: number;
}

/** ADR-0011: skill script captured during the battle. */
export interface BridgeSkillCast {
  atMs: number;
  skillId: string;
}

export interface BridgeQueueEntry {
  idempotencyKey: string;
  mapId: string;
  heroId: string;
  seed: number;
  bestStars: 0 | 1 | 2 | 3;
  previousBestStars: 0 | 1 | 2 | 3;
  /** ADR-0011: the loadout that produced this clear. Required for the edge
   * function's re-simulation to match the client's exact inputs. */
  towers: BridgeTowerRef[];
  /** ADR-0011: hero active casts during this battle. May be empty for
   * passive-only clears. */
  skillScript: BridgeSkillCast[];
  /** Milliseconds since epoch when enqueued. Used to surface "pending" state. */
  enqueuedAt: number;
  /** Number of POST attempts so far. Max 3 per ADR-0008. */
  attempts: number;
}

interface BridgeState {
  linkStatus: BridgeLinkStatus;
  /** OAuth access token (24h). Null when disconnected. */
  token: string | null;
  /** Jubit account username for display. Null when disconnected. */
  jubitUsername: string | null;
  /** Lifetime credits granted, tallied from server-acked POSTs. */
  creditsEarnedLifetime: number;
  /** FIFO queue of pending clear-events to POST. */
  queue: BridgeQueueEntry[];
  /** Last error message, surfaced in Settings. Null when clean. */
  lastError: string | null;

  /** Begin OAuth flow. In Sprint 14 scaffold, returns a stub token. */
  connect: () => Promise<void>;
  disconnect: () => void;
  /** Enqueue a clear-event for POSTing. */
  enqueueClear: (entry: Omit<BridgeQueueEntry, 'enqueuedAt' | 'attempts'>) => void;
  /** Pop the next queue entry regardless of outcome (caller decides). */
  dequeueFirst: () => BridgeQueueEntry | undefined;
  /** Record a successful POST's credit grant. */
  recordGrant: (credits: number) => void;
  /** Record a failure; increments attempts on the head entry or sets lastError. */
  recordFailure: (message: string) => void;
  clearError: () => void;
}

/** True when the bridge is configured with a URL. Under Vite, this reads
 * import.meta.env at build time. Unset → bridge is inert in this build. */
export function isBridgeEnabled(): boolean {
  // Vite exposes env on import.meta.env.
  // Wrap in try-catch to keep the store import-safe under test runners that
  // don't polyfill import.meta.env (vitest handles it; SSR callers may not).
  try {
    const url = import.meta.env.VITE_BRIDGE_URL;
    return typeof url === 'string' && url.length > 0;
  } catch {
    return false;
  }
}

export const useBridgeStore = create<BridgeState>()(
  persist(
    (set, get) => ({
      linkStatus: 'disconnected',
      token: null,
      jubitUsername: null,
      creditsEarnedLifetime: 0,
      queue: [],
      lastError: null,

      async connect() {
        if (!isBridgeEnabled()) {
          // Feature flag off — surface a stable message rather than pretending to work.
          set({
            linkStatus: 'error',
            lastError: 'Bridge not yet configured for this build (VITE_BRIDGE_URL unset).',
          });
          return;
        }
        set({ linkStatus: 'connecting', lastError: null });
        // Sprint 15: real popup OAuth with CSRF state param + origin verification.
        try {
          const { token, username } = await openJubitOAuth();
          set({
            linkStatus: 'connected',
            token,
            jubitUsername: username,
          });
        } catch (err) {
          const code = err instanceof BridgeOAuthError ? err.code : 'unknown';
          const msg = err instanceof Error ? err.message : String(err);
          set({
            linkStatus: 'error',
            lastError: `Link failed (${code}): ${msg}`,
          });
        }
      },

      disconnect() {
        set({
          linkStatus: 'disconnected',
          token: null,
          jubitUsername: null,
          lastError: null,
          // Keep queue + lifetime credits — user can reconnect to continue
        });
      },

      enqueueClear(entry) {
        set((state) => ({
          queue: [
            ...state.queue,
            { ...entry, enqueuedAt: Date.now(), attempts: 0 },
          ],
        }));
      },

      dequeueFirst() {
        const head = get().queue[0];
        if (!head) return undefined;
        set((state) => ({ queue: state.queue.slice(1) }));
        return head;
      },

      recordGrant(credits) {
        set((state) => ({
          creditsEarnedLifetime: state.creditsEarnedLifetime + credits,
          lastError: null,
        }));
      },

      recordFailure(message) {
        set((state) => {
          const [head, ...rest] = state.queue;
          if (!head) return { lastError: message };
          // ADR-0008: max 3 attempts. If exhausted, drop from queue + surface error.
          if (head.attempts + 1 >= 3) {
            return {
              queue: rest,
              lastError: `${message} (dropped after 3 attempts for map ${head.mapId})`,
            };
          }
          return {
            queue: [{ ...head, attempts: head.attempts + 1 }, ...rest],
            lastError: message,
          };
        });
      },

      clearError() {
        set({ lastError: null });
      },
    }),
    {
      // ADR-0011 Sprint 19: v1 → v2 bump because BridgeQueueEntry shape gained
      // towers + skillScript. Pre-v2 queued entries have no towers and would
      // fail the server re-simulation; safe to drop since bridge hasn't shipped
      // live yet.
      name: 'jubuddy:bridge-store:v2',
      version: 2,
      migrate: (persistedState, version) => {
        if (version < 2) {
          // Drop the queue — old shape can't be server-verified anyway. Keep
          // link status + lifetime credits (still valid across shapes).
          const s = (persistedState ?? {}) as Partial<BridgeState>;
          return {
            ...s,
            queue: [],
            lastError: null,
          } as BridgeState;
        }
        return persistedState as BridgeState;
      },
      // Don't persist the transient lastError across reloads — surface fresh.
      partialize: (state) => ({
        linkStatus: state.linkStatus,
        token: state.token,
        jubitUsername: state.jubitUsername,
        creditsEarnedLifetime: state.creditsEarnedLifetime,
        queue: state.queue,
      }),
    },
  ),
);
